import { createClient, type ContentfulClientApi, type EntrySkeletonType, type EntryFieldTypes } from 'contentful';
import type { JobPosting } from '@/types/job';

function getClient(): ContentfulClientApi<undefined> | null {
  const space = import.meta.env.VITE_CONTENTFUL_SPACE_ID;
  const accessToken = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

  if (!space || !accessToken) {
    console.warn(
      'Contentful credentials not configured. Set VITE_CONTENTFUL_SPACE_ID and VITE_CONTENTFUL_ACCESS_TOKEN environment variables.',
    );
    return null;
  }

  return createClient({
    space,
    accessToken,
    environment: import.meta.env.VITE_CONTENTFUL_ENVIRONMENT || 'master',
  });
}

interface JobPostingSkeleton extends EntrySkeletonType {
  contentTypeId: 'jobPosting';
  fields: {
    title: EntryFieldTypes.Text;
    slug: EntryFieldTypes.Text;
    department: EntryFieldTypes.Text;
    location: EntryFieldTypes.Text;
    type: EntryFieldTypes.Text;
    description: EntryFieldTypes.RichText;
    requirements: EntryFieldTypes.RichText;
    salaryRange: EntryFieldTypes.Text;
    postedDate: EntryFieldTypes.Date;
  };
}

export async function getJobPostings(): Promise<JobPosting[]> {
  const client = getClient();
  if (!client) return [];

  try {
    const response = await client.getEntries<JobPostingSkeleton>({
      content_type: 'jobPosting',
      order: ['-fields.postedDate'],
    });

    return response.items.map((item) => ({
      title: item.fields.title as string,
      slug: item.fields.slug as string,
      department: item.fields.department as string,
      location: item.fields.location as string,
      type: item.fields.type as string,
      description: item.fields.description as unknown as JobPosting['description'],
      requirements: item.fields.requirements as unknown as JobPosting['description'],
      salaryRange: (item.fields.salaryRange as string) ?? undefined,
      postedDate: item.fields.postedDate as string,
    }));
  } catch {
    console.error('Failed to fetch job postings from Contentful');
    return [];
  }
}

export async function getJobBySlug(slug: string): Promise<JobPosting | null> {
  const client = getClient();
  if (!client) return null;

  try {
    const response = await client.getEntries<JobPostingSkeleton>({
      content_type: 'jobPosting',
      'fields.slug': slug,
      limit: 1,
    });

    if (response.items.length === 0) return null;

    const item = response.items[0];
    return {
      title: item.fields.title as string,
      slug: item.fields.slug as string,
      department: item.fields.department as string,
      location: item.fields.location as string,
      type: item.fields.type as string,
      description: item.fields.description as unknown as JobPosting['description'],
      requirements: item.fields.requirements as unknown as JobPosting['description'],
      salaryRange: (item.fields.salaryRange as string) ?? undefined,
      postedDate: item.fields.postedDate as string,
    };
  } catch {
    console.error('Failed to fetch job posting from Contentful');
    return null;
  }
}
